import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from '@/lib/router-events';
import { DEFAULT_MEASUREMENT_UNIT, prepareNextPath, prepareUrl, updateSearchLocationInUrl } from '@/utils/urlHelper';
import { queryStringConstants } from '@/constants/queryConstants';
import { resolvePlannerLocale, type Locale } from '@/i18n';
import { GPT_4_OMNI } from '@/constants/ai';

export default function useAISearch(searchTerm: string, isLoggedIn: boolean, locale: Locale, basePath: string) {
  const router = useRouter();
  const path = usePathname();
  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams === null ? undefined : searchParams);
  // eslint-disable-next-line consistent-return
  return async (conversation: string[]): Promise<{ err: boolean; routed: boolean; errorType?: string }> => {
    try {
      // take the query and get filter
      const response = await fetch('/venues/api/ai', {
        method: 'POST',
        body: JSON.stringify({
          conversation,
          currentSearchTerm: searchTerm,
          currentUnit: searchParams.get(queryStringConstants.MEASUREMENT_UNIT) ?? DEFAULT_MEASUREMENT_UNIT,
          version: GPT_4_OMNI
        })
      });
      if (response.ok) {
        const data = await response.json();

        if (data.error) {
          return { err: true, routed: false, ...(data.errorType ? { errorType: data.errorType } : {}) };
        }

        const geoCode = data.geoCode || undefined;
        const additionalParams = [
          { key: queryStringConstants.FILTER, value: data.filter },
          ...(geoCode ? [{ key: queryStringConstants.GEO, value: geoCode }] : [])
        ];

        let url = path;
        if (geoCode) {
          url = updateSearchLocationInUrl(data.location, path);
        }

        let localeForNewURL = locale;

        // update locale according to query only for the guest user
        if (!isLoggedIn && data.locale !== locale) {
          localeForNewURL = resolvePlannerLocale(data.locale);
        }

        const updatedPath = prepareNextPath(basePath, url, localeForNewURL);
        if (params.get('filter') !== data.filter || params.get('geo') !== data.geoCode) {
          router.push(prepareUrl(updatedPath, params, ...additionalParams));
          return { err: false, routed: true };
        }
        return { err: false, routed: false };
      }
      // fallback to error if api response failed
      return { err: true, routed: false };
    } catch (error) {
      return { err: true, routed: false };
    }
  };
}
